import { useEffect, useMemo, useState } from 'react';
import type {
  FetchRecommendationsDataArgs,
  RecommendationsServingConfig,
} from '@noths/polaris-client-listings-api';
import {
  fetchRecommendationsData,
  ListingsRequestAndFilterBuilder,
  ListingsRequestTextFilterBuilder,
  RecommendationsRequestBuilder,
} from '@noths/polaris-client-listings-api';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { MAINLAND_UK_DELIVERY_ZONE } from '@noths/polaris-client-localisation';
import { getUserAttributes } from '@noths/polaris-client-user-configuration';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';
import type { ProductCarouselItem } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { RecommendationsTrackingProduct } from 'src/adapters/listingsAPIToProductCarouselAdapter';
import { transformRecommendedProductToProductCarouselItem } from 'src/adapters/listingsAPIToProductCarouselAdapter';
import type { ServiceRecommendedProduct } from 'src/builders/recommendedProduct';
import { usePromoteAsSale } from 'src/context/discountPresentationMode/DiscountPresentationModeContext';
import { isProd } from 'src/environment';
import { logger } from 'src/utils/serverLogger';

/**
 * Vertex's same-seller model, narrowed further below by a `partner_shortcode`
 * filter so the rail is the seed product's partner's. Named because two decisions
 * key off it: that filter, and whether a card may carry a badge that describes the
 * partner.
 */
const SAME_PARTNER_SERVING_CONFIG = 'pdp_same_seller_2';

const isSinglePartnerRail = (servingConfig: RecommendationsServingConfig) =>
  servingConfig === SAME_PARTNER_SERVING_CONFIG;

const createListingsAPIFilter = (partnerShortCode?: string) => {
  const inStockFilter = new ListingsRequestTextFilterBuilder('availability', ['IN_STOCK']).build();
  const filterCollection = [inStockFilter];

  if (partnerShortCode) {
    const partnerFilter = new ListingsRequestTextFilterBuilder('attributes.partner_shortcode', [
      partnerShortCode,
    ]).build();
    filterCollection.push(partnerFilter);
  }

  return new ListingsRequestAndFilterBuilder().filters(filterCollection).build();
};

export interface UseListingsRecommendationsArgs {
  currencyCode: CurrencyCode;
  deliveryZone?: DeliveryZoneCode;
  partnerShortCode: string;
  productCode: string;
  servingConfig: RecommendationsServingConfig;
  visitorId?: string;
}

type CreateRecommendationsParametersArgs = Pick<
  UseListingsRecommendationsArgs,
  | 'currencyCode'
  | 'deliveryZone'
  | 'productCode'
  | 'servingConfig'
  | 'visitorId'
  | 'partnerShortCode'
> & {
  userId?: string;
};

const createRecommendationsParameters = ({
  currencyCode,
  deliveryZone,
  partnerShortCode,
  productCode,
  servingConfig,
  userId,
  visitorId,
}: CreateRecommendationsParametersArgs): FetchRecommendationsDataArgs => {
  const id = getProductIdFromProductCode(Number(productCode)).toString();
  const requestBuilder = new RecommendationsRequestBuilder();

  requestBuilder.currency(currencyCode);
  /* The zone scopes the `delivery` option the API resolves per product, so it is
     sent on every request rather than applied to the response. Omitting it is not
     neutral: the API reads an absent zone as mainland UK and would quote a non-UK
     customer a UK price. A visitor with no locale header and no `delivery_zone`
     cookie has no zone, which is a real case rather than a gap, and this is the
     one place that decides what that means. Same choice, for the same reason, as
     the PLP grid's `adaptStoreToRequestBody`. */
  requestBuilder.deliveryZone(deliveryZone ?? MAINLAND_UK_DELIVERY_ZONE);
  requestBuilder.pageSize(10);
  requestBuilder.products([{ id }]);

  const andFilter = isSinglePartnerRail(servingConfig)
    ? createListingsAPIFilter(partnerShortCode)
    : createListingsAPIFilter();

  requestBuilder.filter(andFilter);

  const body = requestBuilder.build();

  return {
    body,
    clientSide: true,
    env: isProd() ? 'prod' : 'qa',
    servingConfig,
    timeout: 15000,
    userId,
    visitorId,
  };
};

/**
 * A response and the instant it arrived, held as one value.
 *
 * The instant belongs to the response because every card in a rail counts its
 * estimated delivery from the same moment: the adapter's own `now` default is
 * evaluated per call, so mapping a set without one could date a rail across
 * midnight and show two different dates for the same lead time. Keeping it here
 * rather than in the mapping means a remap for presentation cannot re-date a set.
 */
interface RecommendationsResponse {
  products: ServiceRecommendedProduct[];
  respondedAt: Date;
}

/** One identity for "nothing to track", so the container's callbacks stay stable. */
const NO_PRODUCTS: ServiceRecommendedProduct[] = [];

export const useListingsRecommendations = ({
  currencyCode,
  deliveryZone,
  partnerShortCode,
  productCode,
  servingConfig,
  visitorId,
}: UseListingsRecommendationsArgs) => {
  const promoteAsSale = usePromoteAsSale();
  /**
   * The service response is the only thing held. Both of this hook's return values
   * are derived from it below, so they walk the same array in the same order and
   * can never disagree: a card's index in the carousel is the index of the service
   * product that produced it, which is how the container resolves a click back to
   * something it can track.
   */
  const [response, setResponse] = useState<RecommendationsResponse | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setResponse(null);
        const userAttributes = await getUserAttributes();
        const fetchParams = createRecommendationsParameters({
          currencyCode,
          deliveryZone,
          partnerShortCode,
          productCode,
          servingConfig,
          userId: userAttributes?.sub,
          visitorId,
        });

        const responseData = await fetchRecommendationsData(fetchParams);

        if ('message' in responseData) {
          setResponse({ products: NO_PRODUCTS, respondedAt: new Date() });
          logger.error('Error getting recommendations from listings-api:', responseData.message);
          return;
        }

        setResponse({ products: responseData.products, respondedAt: new Date() });
      } catch (error) {
        setResponse({ products: NO_PRODUCTS, respondedAt: new Date() });
        logger.error('Exception thrown while getting recommendations from listings-api:', error);
      }
    };

    fetchProducts();
  }, [currencyCode, deliveryZone, servingConfig, visitorId, partnerShortCode, productCode]);

  /* Mapped out here rather than inside the fetch so that `promoteAsSale`, which is
     presentation and nothing else, remaps the response already in hand instead of
     joining the dependency list above and refetching the rail. The effect has no
     stale-response guard, so a refetch on a site-wide switch would also be a race
     the older request could win. */
  const recommendedProducts: ProductCarouselItem[] | null = useMemo(
    () =>
      response?.products.map((product) =>
        transformRecommendedProductToProductCarouselItem(product, {
          currency: currencyCode,
          isSinglePartnerRail: isSinglePartnerRail(servingConfig),
          now: response.respondedAt,
          promoteAsSale,
        }),
      ) ?? null,
    [currencyCode, promoteAsSale, response, servingConfig],
  );

  /* Narrowed on the way out to the fields GA reports on, so the container cannot
     quietly start tracking something the event schema has no room for. */
  const productsToTrack: RecommendationsTrackingProduct[] = response?.products ?? NO_PRODUCTS;

  return {
    productsToTrack,
    recommendedProducts,
  };
};
